import { useCallback, useState } from 'react';

import {
    EikaMessageEvent,
    parseCallback,
    postMessageRN,
    runningInRNWebView,
    useNativeEventListener,
    useReactNativeProps,
    WebToRNAction,
} from '@eika-infrastruktur/mobilbank-lib';
import { FcmTokenPayload } from '@eika-infrastruktur/mobilbank-lib/src/webEvents';
import { useQueryClient } from '@eika-infrastruktur/react-query';

import { useCreatePushDevice, useDeactivatePushDevice } from '../../../fetchHooks/usePushDevice';
import { PUSH_NOTIFICATION_RULES_QUERY_KEY } from '../../../fetchHooks/usePushNotificationRules';
import type { CreatePushDeviceRequest } from '../../../generated-types';
import { platformMapper } from '../../../services/pushNotificationService';

type FCMWebActions = 'FCM_TOKEN_FOR_PUSH' | 'FCM_TOKEN_FOR_PUSH_ERROR';

const useTogglePushDevice = () => {
    const { platform } = useReactNativeProps();
    const [nativeError, setNativeError] = useState(false);

    const createPushDevice = useCreatePushDevice();
    const deletePushDevice = useDeactivatePushDevice();
    const queryClient = useQueryClient();

    const onMessage = useCallback(
        (event: EikaMessageEvent<FCMWebActions>) => {
            const data = parseCallback<FCMWebActions>(event.data);
            if (data) {
                switch (data.action) {
                    case 'FCM_TOKEN_FOR_PUSH': {
                        const fcmTokenPayload = data.payload as FcmTokenPayload;
                        if (platform && fcmTokenPayload) {
                            const payload: CreatePushDeviceRequest = {
                                fcmToken: fcmTokenPayload.fcmToken,
                                platformOS: platformMapper(platform),
                                messageType: fcmTokenPayload.messageType ?? 'NOTIFICATION',
                            };
                            createPushDevice.mutate(payload);
                        }
                        break;
                    }
                    case 'FCM_TOKEN_FOR_PUSH_ERROR': {
                        setNativeError(true);
                        break;
                    }
                    default:
                        break;
                }
            }
        },
        [createPushDevice, platform],
    );

    useNativeEventListener(onMessage);

    const onToggle = async (value: boolean) => {
        setNativeError(false);
        if (!runningInRNWebView) {
            setNativeError(true);
            return;
        }
        if (value) {
            postMessageRN({
                action: WebToRNAction.GET_FCM_TOKEN,
                payload: undefined,
            });
        } else {
            await deletePushDevice.mutateAsync();
        }
        await queryClient.invalidateQueries({ queryKey: [PUSH_NOTIFICATION_RULES_QUERY_KEY] });
    };

    return {
        onToggle,
        nativeError,
        isPending: createPushDevice.isPending || deletePushDevice.isPending,
        createPushDeviceError: createPushDevice.error,
        deletePushDeviceError: deletePushDevice.error,
    };
};

export default useTogglePushDevice;
